import { useState, useCallback } from "react";
import { theme, fonts } from "../theme";
import { Card, Button } from "./ui";

/* ── Toast ─────────────────────────────────────── */
let _toastFn = null;
export function toast(msg, type = "success") { _toastFn?.(msg, type); }
export function ToastContainer() {
  const [items, setItems] = useState([]);
  _toastFn = useCallback((msg, type) => {
    const id = Date.now();
    setItems(p => [...p, { id, msg, type }]);
    setTimeout(() => setItems(p => p.filter(t => t.id !== id)), 3500);
  }, []);
  if (!items.length) return null;
  return <div style={{ position: "fixed", top: 16, right: 16, zIndex: 9999, display: "flex", flexDirection: "column", gap: 8 }}>
    {items.map(t => <div key={t.id} style={{
      animation: "toastIn 0.3s ease-out", padding: "10px 18px", borderRadius: 10, fontSize: 12, fontWeight: 600,
      background: t.type === "error" ? theme.redLight : t.type === "warn" ? theme.amberLight : theme.greenLight,
      color: t.type === "error" ? theme.redText : t.type === "warn" ? theme.amberText : theme.greenText,
      boxShadow: "0 4px 16px rgba(0,0,0,0.1)", fontFamily: fonts.body, maxWidth: 360,
    }}>{t.msg}</div>)}
  </div>;
}

/* ── Modal ─────────────────────────────────────── */
let _modalFn = null;
export function showModal(title, msg, onConfirm) { _modalFn?.(title, msg, onConfirm); }
export function ModalContainer() {
  const [state, setState] = useState(null);
  _modalFn = useCallback((title, msg, onConfirm) => setState({ title, msg, onConfirm }), []);
  if (!state) return null;
  return <div style={{ position: "fixed", inset: 0, zIndex: 9998, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.3)", backdropFilter: "blur(2px)" }} onClick={() => setState(null)}>
    <div onClick={e => e.stopPropagation()} className="sl-fadein" style={{ background: theme.white, borderRadius: 16, padding: "28px 32px", maxWidth: 400, width: "90%", boxShadow: "0 16px 48px rgba(0,0,0,0.12)" }}>
      <div style={{ fontSize: 16, fontWeight: 800, color: theme.text, marginBottom: 8 }}>{state.title}</div>
      <div style={{ fontSize: 13, color: theme.text2, lineHeight: 1.6, marginBottom: 24 }}>{state.msg}</div>
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <Button className="sl-btn" onClick={() => setState(null)}>Abbrechen</Button>
        <Button primary className="sl-btn" onClick={() => { state.onConfirm(); setState(null); }}>Best&auml;tigen</Button>
      </div>
    </div>
  </div>;
}

/* ── Skeleton ──────────────────────────────────── */
export function Skeleton({ w = "100%", h = 16, r = 8, style: s }) {
  return <div className="sl-skel" style={{ width: w, height: h, borderRadius: r, ...s }} />;
}
export function SkeletonCards({ n = 4 }) {
  return <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))", gap: 12 }}>
    {Array.from({ length: n }, (_, i) => <Card key={i} style={{ padding: 20 }}><Skeleton h={14} w="40%" style={{ marginBottom: 12 }} /><Skeleton h={28} w="60%" style={{ marginBottom: 16 }} /><div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}><Skeleton h={12} /><Skeleton h={12} /><Skeleton h={12} /></div></Card>)}
  </div>;
}

/* ── CSV Export ─────────────────────────────────── */
export function downloadCSV(rows, filename) {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]);
  const csv = [headers.join(","), ...rows.map(r => headers.map(h => { const v = r[h]; return typeof v === "string" && v.includes(",") ? `"${v}"` : v; }).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

/* ── SVG Empty State Icons ─────────────────────── */
export function EmptyIcon({ type }) {
  const s = { width: 64, height: 64 };
  if (type === "chart") return <svg viewBox="0 0 64 64" style={s}><rect x="8" y="8" width="48" height="48" rx="8" fill={theme.tint} stroke={theme.border} strokeWidth="1.5"/><polyline points="16,42 24,30 32,36 40,22 48,28" fill="none" stroke={theme.green} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/><circle cx="16" cy="42" r="2" fill={theme.green}/><circle cx="48" cy="28" r="2" fill={theme.green}/></svg>;
  if (type === "bolt") return <svg viewBox="0 0 64 64" style={s}><rect x="8" y="8" width="48" height="48" rx="8" fill={theme.tint} stroke={theme.border} strokeWidth="1.5"/><path d="M34 16L22 36h10l-2 12 14-20H32l2-12z" fill={theme.amberLight} stroke={theme.amber} strokeWidth="1.5" strokeLinejoin="round"/></svg>;
  if (type === "list") return <svg viewBox="0 0 64 64" style={s}><rect x="8" y="8" width="48" height="48" rx="8" fill={theme.tint} stroke={theme.border} strokeWidth="1.5"/><line x1="20" y1="22" x2="44" y2="22" stroke={theme.text3} strokeWidth="2" strokeLinecap="round"/><line x1="20" y1="32" x2="38" y2="32" stroke={theme.text4} strokeWidth="2" strokeLinecap="round"/><line x1="20" y1="42" x2="42" y2="42" stroke={theme.text4} strokeWidth="2" strokeLinecap="round"/></svg>;
  if (type === "shield") return <svg viewBox="0 0 64 64" style={s}><rect x="8" y="8" width="48" height="48" rx="8" fill={theme.tint} stroke={theme.border} strokeWidth="1.5"/><path d="M32 18l12 5v10c0 8-12 13-12 13s-12-5-12-13V23l12-5z" fill="none" stroke={theme.blue} strokeWidth="2" strokeLinejoin="round"/></svg>;
  return <svg viewBox="0 0 64 64" style={s}><rect x="8" y="8" width="48" height="48" rx="8" fill={theme.tint} stroke={theme.border} strokeWidth="1.5"/></svg>;
}

/* ── Sortable Table Header ─────────────────────── */
export function SortTh({ children, sortKey, sort, setSort, style: s }) {
  const active = sort.key === sortKey; const asc = active && sort.asc;
  return <th className="sl-sort" onClick={() => setSort({ key: sortKey, asc: active ? !sort.asc : false })} style={{ ...s, padding: "7px 10px", fontSize: 9, fontWeight: 700, color: active ? theme.blueText : theme.text4, textTransform: "uppercase", textAlign: "left", whiteSpace: "nowrap", cursor: "pointer", userSelect: "none" }}>{children} {active ? (asc ? "\u25B2" : "\u25BC") : ""}</th>;
}
export function sortRows(rows, sort) {
  if (!sort.key) return rows;
  return [...rows].sort((a, b) => { const va = a[sort.key] ?? 0, vb = b[sort.key] ?? 0; return sort.asc ? (va > vb ? 1 : -1) : (va < vb ? 1 : -1); });
}
